/**
 * PWA vault auto-lock prefs — clears in-memory vault key after idle / background.
 * Encrypted IndexedDB rows remain sealed (at-rest cipher cleared with the key).
 */

export interface LockPrefs {
  /** Minutes of idle / background before lock. 0 = never auto-lock. 0.25 = "Right away" (15 s). */
  autoLockMinutes: number;
  /** Start the auto-lock timer when the tab is hidden. */
  lockOnHide: boolean;
}

const KEY = "veto.lock.prefs";

export const DEFAULT_LOCK_PREFS: LockPrefs = {
  autoLockMinutes: 5,
  lockOnHide: true,
};

/** "Right away" keeps 15 seconds (phone parity) so picking a file doesn't lock you out. */
export const RIGHT_AWAY_MINUTES = 0.25;

/** Same choices and order as the phone app (0 = Never, last). */
export const AUTO_LOCK_CHOICES: readonly number[] = [RIGHT_AWAY_MINUTES, 1, 5, 15, 30, 60, 0];

/** While Veto is open and in front, inactivity locks after at least this long (a short
 *  "Right away" applies to the background only — reading a long chat must not lock you out). */
export const MIN_FOREGROUND_IDLE_MS = 60_000;

export function autoLockLabel(minutes: number): string {
  if (!minutes || minutes <= 0) return "Never";
  if (minutes === RIGHT_AWAY_MINUTES) return "Right away";
  if (minutes === 1) return "After 1 minute";
  if (minutes === 60) return "After 1 hour";
  if (minutes < 1) return `After ${Math.round(minutes * 60)} seconds`;
  return `After ${minutes} minutes`;
}

export function loadLockPrefs(): LockPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_LOCK_PREFS };
    const parsed = JSON.parse(raw) as Partial<LockPrefs>;
    const minutes = Number(parsed.autoLockMinutes);
    return {
      autoLockMinutes:
        Number.isFinite(minutes) && minutes >= 0 ? minutes : DEFAULT_LOCK_PREFS.autoLockMinutes,
      lockOnHide: parsed.lockOnHide ?? DEFAULT_LOCK_PREFS.lockOnHide,
    };
  } catch {
    return { ...DEFAULT_LOCK_PREFS };
  }
}

export function saveLockPrefs(prefs: LockPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* private mode — session-only */
  }
}
